import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { TooltipProps } from './types';

export function useTooltipAnchorEvents<T = Element>({
  hideTooltip,
  showTooltip,
}: {
  hideTooltip: () => void;
  showTooltip: () => void;
}): {
  onMouseDown: React.MouseEventHandler<T>;
  onMouseUp: React.MouseEventHandler<T>;
  onMouseEnter: React.MouseEventHandler<T>;
  onMouseLeave: React.MouseEventHandler<T>;
  onFocus: React.FocusEventHandler<T>;
  onBlur: React.FocusEventHandler<T>;
} {
  const mouseIn = useRef<boolean>(false);
  const focused = useRef<EventTarget>();

  // tooltips should close when the escape key is pressed
  // https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Roles/tooltip_role#keyboard_interactions
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent | React.KeyboardEvent) => {
      if (e.key === 'Escape') {
        focused.current = undefined;
        hideTooltip();
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [hideTooltip]);

  return useMemo(
    () => ({
      onMouseDown: hideTooltip,
      onMouseUp: hideTooltip,
      onMouseEnter: () => {
        mouseIn.current = true;
        showTooltip();
      },
      onMouseLeave: () => {
        mouseIn.current = false;
        focused.current = undefined;
        hideTooltip();
      },
      onFocus: e => {
        focused.current = e.target;
        if (!mouseIn.current) {
          showTooltip();
        }
      },
      onBlur: e => {
        // Only hide on blur when focus was the initiator of the tooltip
        // this fixes issues when tooltip children are focusable and are
        // clicked.
        if (e.target == focused.current) {
          focused.current = undefined;
          hideTooltip();
        }
      },
    }),
    [hideTooltip, showTooltip],
  );
}

export function useTooltip<T = Element>(
  options: Pick<TooltipProps, 'disabled' | 'persistDelay' | 'showDelay'>,
) {
  const { disabled = false, persistDelay = 100, showDelay = 100 } = options;
  const [visible, setVisible] = useState<boolean>(false);
  const anchorRef = useRef<T | null>(null);
  const mountedRef = useRef<boolean>(false);
  const confirmHideRef = useRef<boolean>();

  useEffect(() => {
    mountedRef.current = true;

    return () => {
      mountedRef.current = false;
      confirmHideRef.current = false;
    };
  }, []);

  const showTooltip = useCallback(() => {
    if (mountedRef.current && !disabled) setVisible(true);
  }, [disabled]);

  const hideTooltip = useCallback(() => {
    if (mountedRef.current) setVisible(false);
  }, []);

  // called to confirm that the tooltip should stay open
  const persistTooltip = useCallback(() => {
    confirmHideRef.current = false;
  }, []);

  // wait up to `persistDelay` before hiding - if `persistDelay` is undefined or 0, close immediately.
  const maybeDeferHide = useCallback(() => {
    confirmHideRef.current = true;

    if (!persistDelay) {
      hideTooltip();
      return;
    }

    setTimeout(() => {
      if (confirmHideRef.current) hideTooltip();
    }, persistDelay);
  }, [hideTooltip, persistDelay]);

  // wait up to `showDelay` before showing; prevents unwanted tooltips
  const maybeDeferShow = useCallback(() => {
    confirmHideRef.current = false;

    setTimeout(() => {
      if (!confirmHideRef.current && !disabled) showTooltip();
    }, showDelay);
  }, [disabled, showDelay, showTooltip]);

  useEffect(() => {
    if (disabled) hideTooltip();
  }, [disabled, hideTooltip]);

  const anchorEvents = useTooltipAnchorEvents({
    hideTooltip: maybeDeferHide,
    showTooltip: maybeDeferShow,
  });

  return {
    anchorRef,
    anchorEvents,
    visible,
    showTooltip: maybeDeferShow,
    hideTooltip: maybeDeferHide,
    persistTooltip,
  };
}
